import type { ConversationStopCategory } from '@/services/conversations.api'

export type UnreachableChannel = 'SMS' | 'EMAIL'

export interface TransferredStopDisplay {
  badgeLabel: string
  badgeColor: string
  tooltip: string
  handoffLabel: string
  readOnlyMessage: string
  borderColor: string
}

export function formatEscalationSourceLabel(escalationSource?: string): string | undefined {
  switch (escalationSource) {
    case 'no_valid_channel':
      return 'No valid contact channel'
    case 'consent_not_granted':
      return 'SMS consent not granted'
    default:
      return undefined
  }
}

export function getTransferredStopDisplay(input: {
  stopCategory?: ConversationStopCategory
  escalationSource?: string
  unreachableChannels?: UnreachableChannel[]
}): TransferredStopDisplay {
  const { stopCategory = 'ESCALATED', escalationSource, unreachableChannels = [] } = input

  if (stopCategory === 'CRM_STOP') {
    return {
      badgeLabel: 'Stopped',
      badgeColor: 'red',
      tooltip: 'CRM disabled AI follow-up for this customer',
      handoffLabel: 'Stopped by CRM',
      readOnlyMessage: 'View only — CRM stopped AI follow-up.',
      borderColor: 'var(--mantine-color-red-5)',
    }
  }

  if (stopCategory === 'UNREACHABLE') {
    if (escalationSource === 'consent_not_granted') {
      return {
        badgeLabel: 'Unreachable',
        badgeColor: 'orange',
        tooltip: 'SMS consent not granted — automated SMS is not permitted',
        handoffLabel: 'Unreachable — SMS consent not granted',
        readOnlyMessage: 'View only — SMS consent was not granted; dealer follow-up required.',
        borderColor: 'var(--mantine-color-orange-5)',
      }
    }

    const bothUnreachable = unreachableChannels.includes('SMS') && unreachableChannels.includes('EMAIL')
    const smsOnlyUnreachable = unreachableChannels.includes('SMS') && !unreachableChannels.includes('EMAIL')
    const emailOnlyUnreachable = unreachableChannels.includes('EMAIL') && !unreachableChannels.includes('SMS')

    if (bothUnreachable) {
      return {
        badgeLabel: 'Unreachable',
        badgeColor: 'orange',
        tooltip: 'No reachable SMS or email — dealer follow-up required',
        handoffLabel: 'Unreachable — no valid contact channel',
        readOnlyMessage: 'View only — no reachable automated contact channel.',
        borderColor: 'var(--mantine-color-orange-5)',
      }
    }

    if (smsOnlyUnreachable) {
      return {
        badgeLabel: 'Unreachable',
        badgeColor: 'orange',
        tooltip: 'SMS undeliverable (e.g. landline or invalid number). Email may still be available.',
        handoffLabel: 'Unreachable — SMS undeliverable',
        readOnlyMessage: 'View only — SMS could not be delivered; dealer follow-up required.',
        borderColor: 'var(--mantine-color-orange-5)',
      }
    }

    if (emailOnlyUnreachable) {
      return {
        badgeLabel: 'Unreachable',
        badgeColor: 'orange',
        tooltip: 'Email unreachable for automated delivery. SMS may still be available.',
        handoffLabel: 'Unreachable — email unreachable',
        readOnlyMessage: 'View only — email could not be used for automated outreach.',
        borderColor: 'var(--mantine-color-orange-5)',
      }
    }

    return {
      badgeLabel: 'Unreachable',
      badgeColor: 'orange',
      tooltip: formatEscalationSourceLabel(escalationSource) ?? 'Contact channel unavailable for automated delivery',
      handoffLabel: 'Unreachable — dealer follow-up required',
      readOnlyMessage: 'View only — contact channel unavailable for automated delivery.',
      borderColor: 'var(--mantine-color-orange-5)',
    }
  }

  return {
    badgeLabel: 'Escalated',
    badgeColor: 'red',
    tooltip: 'Handed off to the sales team for follow-up',
    handoffLabel: 'Handed off to human',
    readOnlyMessage: 'View only — conversation escalated to the sales team.',
    borderColor: 'var(--mantine-color-red-5)',
  }
}

export function formatSystemEscalationTooltip(metadata?: {
  escalationReason?: string
  escalationCategory?: string
  systemGenerated?: boolean
}): string {
  if (!metadata?.systemGenerated) {
    return 'System-generated escalation message'
  }

  const reasonLabel = formatEscalationSourceLabel(metadata.escalationReason) ?? metadata.escalationReason
  if (metadata.escalationCategory && reasonLabel) {
    return `${reasonLabel} (${metadata.escalationCategory.replace(/_/g, ' ')})`
  }
  return reasonLabel ?? 'System-generated escalation message'
}
